import { useId } from 'react'
import { Checkbox } from '@/shared/components/ui/checkbox'

type CheckboxGroupProps<T extends string> = {
  title: string
  /** When true, keep the legend for a11y but hide it visually (e.g. accordion trigger already shows the title). */
  hideTitle?: boolean
  values: readonly T[]
  selected: string[]
  labelFor: (value: T) => string
  onToggle: (value: T) => void
}

export function CheckboxGroup<T extends string>({
  title,
  hideTitle = false,
  values,
  selected,
  labelFor,
  onToggle,
}: CheckboxGroupProps<T>) {
  // Radix renders a button, not an input, so the label needs an explicit htmlFor.
  const groupId = useId()

  return (
    <fieldset className="space-y-2">
      <legend className={hideTitle ? 'sr-only' : 'text-sm font-medium text-foreground'}>{title}</legend>
      <div className="grid gap-2">
        {values.map((value) => (
          <label
            key={value}
            htmlFor={`${groupId}-${value}`}
            className="flex min-h-9 cursor-pointer items-center gap-2 rounded-md border border-border px-3 text-sm"
          >
            <Checkbox
              id={`${groupId}-${value}`}
              checked={selected.includes(value)}
              onCheckedChange={() => onToggle(value)}
            />
            <span>{labelFor(value)}</span>
          </label>
        ))}
      </div>
    </fieldset>
  )
}
